import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useUpdateLead } from '@/shared/api/leads'
import { refetchFocusLists } from '@/shared/api/leadLists'
import { useCanEdit } from '@/shared/write-access'
import { fmtDate } from '@/shared/lib/format'
import type { Lead } from '@/shared/types'
import { buildLocalIso } from '@/features/leads/workflow/leadUtils'
import { daysOverdue, overdueLabel } from '@/features/leads/workflow/overdue'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'

function initialFields(lead: Lead) {
  const at = lead.nextContactAt
  if (!at) return { date: '', time: '' }
  if (!lead.isCallbackAlert) return { date: at.slice(0, 10), time: '' }
  const local = new Date(at)
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    date: `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(local.getDate())}`,
    time: `${pad(local.getHours())}:${pad(local.getMinutes())}`,
  }
}

// Att bara boka om datumet får aldrig logga ett samtal eller ändra stadie, betyg eller bokning.
export default function NextContactEditor({ lead }: { lead: Lead }) {
  const canEdit = useCanEdit()
  const mutation = useUpdateLead()
  const qc = useQueryClient()
  const pending = useRef(false)
  const [fields, setFields] = useState(() => initialFields(lead))
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setFields(initialFields(lead))
  }, [lead.id, lead.nextContactAt, lead.isCallbackAlert])

  async function save(clear = false) {
    if (!canEdit || pending.current || (!clear && !fields.date)) return
    pending.current = true
    setError(null)
    setSaved(false)
    try {
      await mutation.mutateAsync({
        id: lead.id,
        patch: {
          nextContactAt: clear ? null : fields.time
            ? buildLocalIso(fields.date, fields.time) : fields.date,
          isCallbackAlert: !clear && !!fields.time,
        },
      })
      // Ett rensat eller flyttat datum kan föra tillbaka en lead i Focus,
      // även en som saknades i dess cachade lista innan den här ändringen.
      // Tabellen har redan fått raden från svaret och hämtas inte om.
      void refetchFocusLists(qc)
      setSaved(true)
    } catch {
      setError('Kunde inte spara. Dina ändringar finns kvar — försök igen.')
    } finally {
      pending.current = false
    }
  }

  if (!canEdit) {
    const overdue = daysOverdue(lead.nextContactAt, Date.now())
    return (
      <span>
        {lead.nextContactAt ? fmtDate(lead.nextContactAt, { time: !!lead.isCallbackAlert }) : '—'}
        {overdue > 0 && <span className="text-danger font-semibold"> · {overdueLabel(overdue)}</span>}
        {lead.nextContactNote && ` · ${lead.nextContactNote}`}
      </span>
    )
  }

  const current = initialFields(lead)
  const changed = fields.date !== current.date || fields.time !== current.time
  return (
    <div>
      <fieldset disabled={mutation.isPending} className="border-0 p-0 m-0 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" aria-label="Nästa kontaktdatum" value={fields.date}
            onChange={e => { setFields(f => ({ ...f, date: e.target.value })); setSaved(false) }} />
          <label className="flex items-center gap-1.5 text-xs">
            <Checkbox checked={!!fields.time}
              onCheckedChange={v => { setFields(f => ({ ...f, time: v === true ? (f.time || '10:00') : '' })); setSaved(false) }} />
            Specifik tid
          </label>
          {fields.time && <input type="time" aria-label="Nästa kontakttid" value={fields.time}
            onChange={e => { setFields(f => ({ ...f, time: e.target.value })); setSaved(false) }} />}
          <Button type="button" size="sm" variant="outline" disabled={!fields.date || !changed}
            loading={mutation.isPending}
            onClick={() => { void save() }}>
            Spara datum
          </Button>
          {lead.nextContactAt && (
            <Button type="button" size="sm" variant="ghost"
              onClick={() => { void save(true) }}>
              Rensa datum
            </Button>
          )}
        </div>
      </fieldset>
      {lead.nextContactNote && <p className="text-xs text-ink-500 mt-2 mb-0">{lead.nextContactNote}</p>}
      {error && <p role="alert" className="text-xs text-red-700 mt-2 mb-0">{error}</p>}
      {saved && <p role="status" className="text-xs text-ink-500 mt-2 mb-0">Datum sparat.</p>}
    </div>
  )
}
